import { ragApi, RagApiError } from '@/services/ApiService'
import { log } from '@/lib/logger'

/**
 * Base service class providing common functionality for all API services
 */
export abstract class BaseService {
  protected api = ragApi
  protected serviceName: string

  constructor(serviceName: string) {
    this.serviceName = serviceName
  }

  /**
   * Enhanced error handling with service-specific logging
   */
  protected handleError(error: unknown, operation: string): never {
    const message = error instanceof RagApiError 
      ? error.message 
      : `${this.serviceName} ${operation} failed: ${error}`
    
    log.error(`${this.serviceName} ${operation}`, error, { 
      component: 'BaseService', 
      function: 'handleError',
      service: this.serviceName,
      operation 
    })
    throw new Error(message)
  }

  /**
   * Log service operations for debugging
   */
  protected log(operation: string, data?: unknown) {
    log.debug(`${this.serviceName} ${operation}`, data, { 
      component: 'BaseService', 
      function: 'log',
      service: this.serviceName,
      operation 
    })
  }

  /**
   * Validate required parameters
   */
  protected validateRequired(params: Record<string, unknown>, requiredFields: string[]) {
    const missing = requiredFields.filter(field => 
      params[field] === undefined || params[field] === null || params[field] === ''
    )
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`)
    }
  }

  /**
   * Safe async operation with error handling
   */
  protected async safeOperation<T>(
    operation: string,
    fn: () => Promise<T>,
    errorMessage?: string
  ): Promise<T> {
    try {
      this.log(`Starting ${operation}`)
      const result = await fn()
      this.log(`Completed ${operation}`, { success: true })
      return result
    } catch (error) {
      const message = errorMessage || `${operation} failed`
      this.handleError(error, message)
    }
  }

  /**
   * Transform API response with error handling
   */
  protected async transformResponse<TApi, TUI>(
    apiResponse: Promise<TApi>,
    transformer: (apiData: TApi) => TUI,
    operation: string
  ): Promise<TUI> {
    try {
      const data = await apiResponse
      return transformer(data)
    } catch (error) {
      this.handleError(error, operation)
    }
  }

  /**
   * Batch operation helper
   */
  protected async batchOperation<T, R>(
    items: T[],
    operation: (item: T) => Promise<R>,
    batchSize: number = 5
  ): Promise<R[]> {
    const results: R[] = []
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize)
      const batchResults = await Promise.all(
        batch.map(item => operation(item))
      )
      results.push(...batchResults)
    }
    
    return results
  }
}
